import { useEffect, useMemo, useState } from "react";

function prefersReducedMotion() {
  return Boolean(
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
}

/**
 * Reveals `text` at a steady `speed` (ms per character) while `active` is true and resets when it
 * turns false. Returns the typed part and the not-yet-typed rest, so callers can keep the rest in
 * the layout (invisible) and avoid reflowing lines while typing.
 */
export default function useTypewriter(
  text,
  active,
  { speed = 14, delay = 220 } = {},
) {
  const chars = useMemo(() => Array.from(text), [text]);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) {
      setCount(0);
      return;
    }
    if (prefersReducedMotion()) {
      setCount(chars.length);
      return;
    }
    const start = performance.now() + delay;
    let timer;
    // Derive the count from elapsed time so slow renders don't stretch the pace.
    const tick = () => {
      const n = Math.min(
        chars.length,
        Math.floor((performance.now() - start) / speed) + 1,
      );
      setCount(n);
      if (n < chars.length) timer = setTimeout(tick, speed);
    };
    timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [active, chars, speed, delay]);
  return {
    typed: chars.slice(0, count).join(""),
    rest: chars.slice(count).join(""),
    done: count >= chars.length,
  };
}
